import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Empty } from 'antd';
import { PlusOutlined, MinusOutlined, FullscreenOutlined, AimOutlined } from '@ant-design/icons';
import TieringService, { TieringPartner } from '../../services/TieringService';
import styles from './TierDiagram.module.css';

interface TierNode {
  entityId: number;
  entityName: string;
  entityType: string;
  category: string;
  einSsn: string;
  ownershipPct: number;
  tierLevel: number;
  isCluster: boolean;
  clusterCount: number;
  clusterCategory: string | null;
  children: TierNode[];
}

interface D3TierDiagramProps {
  rootEntityId: number;
  renderMode: 'full' | 'clustered' | 'canvas';
  onClusterClick?: (category: string) => void;
}

type HierarchyNode = d3.HierarchyPointNode<TierNode>;
type HierarchyLink = d3.HierarchyPointLink<TierNode>;

const TRUNCATE_LEN = 18;
const truncate = (s: string) => (s.length > TRUNCATE_LEN ? s.slice(0, 16) + '…' : s);

function RootNode({ node }: { node: HierarchyNode }) {
  return (
    <g transform={`translate(${node.x},${node.y})`}>
      {/* Radial glow ring behind root */}
      <circle
        r={90}
        fill="none"
        stroke="#F26522"
        strokeWidth={1}
        strokeOpacity={0.15}
        filter="url(#rootGlow)"
      />
      <circle r={72} fill="rgba(242,101,34,0.06)" />
      <rect
        x={-104} y={-54}
        width={208} height={108}
        rx={14}
        fill="none"
        stroke="rgba(242,101,34,0.20)"
        strokeWidth={1}
      />
      <rect
        x={-100} y={-50} width={200} height={100} rx={12}
        fill="rgba(242,101,34,0.12)" stroke="#F26522" strokeWidth={1.5}
      />
      <text y={-20} textAnchor="middle" fontSize={10} fontWeight={600}
        fill="#F26522" letterSpacing={1.5} fontFamily="Inter, sans-serif">
        {(node.data.entityType || '').toUpperCase()}
      </text>
      <text y={2} textAnchor="middle" fontSize={14} fontWeight={700}
        fill="#FFFFFF" fontFamily="Inter, sans-serif">
        {truncate(node.data.entityName)}
      </text>
      <text y={22} textAnchor="middle" fontSize={11}
        fill="rgba(255,255,255,0.40)" fontFamily="Inter, sans-serif">
        {node.data.einSsn}
      </text>
    </g>
  );
}

function ClusterNodeEl({ node, onClusterClick, isHovered, isExpanded, onEnter, onLeave }: {
  node: HierarchyNode;
  onClusterClick?: () => void;
  isHovered: boolean;
  isExpanded: boolean;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const category = node.data.clusterCategory ?? node.data.category ?? '';
  return (
    <g
      transform={`translate(${node.x}, ${node.y})`}
      style={{ cursor: 'pointer' }}
      onClick={onClusterClick}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <title>{isExpanded ? `Collapse ${category}` : `Click to expand ${category} partners`}</title>
      {isHovered && (
        <ellipse
          cx={0} cy={4}
          rx={80} ry={24}
          fill="rgba(242,101,34,0.10)"
          filter="url(#rootGlow)"
        />
      )}
      <rect
        x={-80} y={-42}
        width={160} height={90}
        rx={36}
        fill={isExpanded ? 'rgba(242,101,34,0.18)' : 'rgba(20,30,60,0.92)'}
        stroke={isHovered ? '#F26522' : 'rgba(242,101,34,0.30)'}
        strokeWidth={isHovered ? 2 : 1}
      />
      <text
        y={-14}
        textAnchor="middle"
        fontSize={11}
        fontWeight={500}
        fill="rgba(255,255,255,0.70)"
        fontFamily="Inter, sans-serif"
      >
        {category.length > 14 ? category.slice(0, 12) + '…' : category}
      </text>
      <text
        y={8}
        textAnchor="middle"
        fontSize={22}
        fontWeight={800}
        fill="#F26522"
        fontFamily="Inter, sans-serif"
      >
        {isExpanded ? '−' : node.data.clusterCount}
      </text>
      <text
        y={26}
        textAnchor="middle"
        fontSize={9}
        fontWeight={600}
        fill="rgba(255,255,255,0.30)"
        fontFamily="Inter, sans-serif"
        letterSpacing={1.5}
      >
        PARTNERS
      </text>
      <text
        y={42}
        textAnchor="middle"
        fontSize={9}
        fill="rgba(242,101,34,0.60)"
        fontFamily="Inter, sans-serif"
      >
        {isExpanded ? '▲ collapse' : '▼ expand'}
      </text>
    </g>
  );
}

function ChildNodeEl({ node, onClusterClick, isHovered, onEnter, onLeave }: {
  node: HierarchyNode;
  onClusterClick?: (category: string) => void;
  isHovered: boolean;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const isPartnership =
    (node.data.entityType || '').toLowerCase().includes('partnership') ||
    (node.data.entityType || '').toLowerCase().includes('fund');

  return (
    <g
      transform={`translate(${node.x},${node.y})`}
      style={{ cursor: 'pointer' }}
      onClick={() => onClusterClick?.(node.data.category)}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <rect
        x={-80} y={-44} width={160} height={88} rx={10}
        fill={isPartnership ? 'rgba(26,39,68,0.85)' : 'rgba(15,25,50,0.85)'}
        stroke={
          isHovered
            ? 'rgba(242,101,34,0.80)'
            : isPartnership
              ? 'rgba(99,179,237,0.60)'
              : 'rgba(255,255,255,0.15)'
        }
        strokeWidth={isHovered ? 1.5 : 1}
      />
      <text y={-20} textAnchor="middle" fontSize={10} fontWeight={600}
        letterSpacing={1.5}
        fill={isPartnership ? 'rgba(99,179,237,0.80)' : 'rgba(255,255,255,0.45)'}
        fontFamily="Inter, sans-serif">
        {(node.data.entityType || '').toUpperCase()}
      </text>
      <text y={2} textAnchor="middle" fontSize={13} fontWeight={600}
        fill="#FFFFFF" fontFamily="Inter, sans-serif">
        {truncate(node.data.entityName)}
      </text>
      {node.data.ownershipPct > 0 && (
        <>
          <rect x={-24} y={10} width={48} height={20} rx={4}
            fill="rgba(242,101,34,0.20)" stroke="rgba(242,101,34,0.50)" strokeWidth={0.5} />
          <text y={24} textAnchor="middle" fontSize={11} fontWeight={700}
            fill="#F26522" fontFamily="Inter, sans-serif">
            {node.data.ownershipPct.toFixed(1)}%
          </text>
        </>
      )}
      <text y={44} textAnchor="middle" fontSize={10}
        fill="rgba(255,255,255,0.30)" fontFamily="Inter, sans-serif">
        {node.data.einSsn}
      </text>
    </g>
  );
}

const controlBtnStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  background: 'rgba(26,39,68,0.90)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  color: 'rgba(255,255,255,0.80)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  backdropFilter: 'blur(8px)',
  transition: 'all 0.15s',
};

const fitToScreen = (
  svgRef: React.RefObject<SVGSVGElement>,
  zoomRef: React.MutableRefObject<d3.ZoomBehavior<SVGSVGElement, unknown> | null>
) => {
  if (!svgRef.current || !zoomRef.current) return;
  const svg = d3.select(svgRef.current);
  const g = svg.select<SVGGElement>('g.zoom-layer');
  const gNode = g.node();
  if (!gNode) return;
  requestAnimationFrame(() => {
    try {
      const bounds = gNode.getBBox();
      if (bounds.width === 0) return;
      const w = svgRef.current!.clientWidth;
      const h = svgRef.current!.clientHeight;
      const scale = Math.min(
        0.85,
        (w - 80) / bounds.width,
        (h - 80) / bounds.height
      );
      const tx = w / 2 - scale * (bounds.x + bounds.width / 2);
      const ty = 40;
      svg.transition().duration(500)
        .call(zoomRef.current!.transform,
          d3.zoomIdentity.translate(tx, ty).scale(scale));
    } catch {}
  });
};

const ZoomControls = ({
  svgRef,
  zoomRef,
}: {
  svgRef: React.RefObject<SVGSVGElement>;
  zoomRef: React.MutableRefObject<d3.ZoomBehavior<SVGSVGElement, unknown> | null>;
}) => (
  <div style={{
    position: 'absolute',
    bottom: 24,
    left: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    zIndex: 10,
  }}>
    <button
      onClick={() => {
        if (!svgRef.current || !zoomRef.current) return;
        d3.select(svgRef.current)
          .transition().duration(300)
          .call(zoomRef.current.scaleBy, 1.4);
      }}
      style={controlBtnStyle}
      title="Zoom in"
    >
      <PlusOutlined />
    </button>
    <button
      onClick={() => {
        if (!svgRef.current || !zoomRef.current) return;
        d3.select(svgRef.current)
          .transition().duration(300)
          .call(zoomRef.current.scaleBy, 0.7);
      }}
      style={controlBtnStyle}
      title="Zoom out"
    >
      <MinusOutlined />
    </button>
    <button
      onClick={() => fitToScreen(svgRef, zoomRef)}
      style={{ ...controlBtnStyle, marginTop: 8 }}
      title="Fit to screen"
    >
      <FullscreenOutlined />
    </button>
    <button
      onClick={() => {
        if (!svgRef.current || !zoomRef.current) return;
        d3.select(svgRef.current)
          .transition().duration(400)
          .call(zoomRef.current.transform,
            d3.zoomIdentity.translate(
              svgRef.current.clientWidth / 2, 80
            ).scale(0.8));
      }}
      style={controlBtnStyle}
      title="Center on root"
    >
      <AimOutlined />
    </button>
  </div>
);

const D3TierDiagram: React.FC<D3TierDiagramProps> = ({
  rootEntityId,
  renderMode,
  onClusterClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const [treeData, setTreeData] = useState<TierNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState({ w: 800, h: 600 });
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());
  const [clusterPartners, setClusterPartners] = useState<Record<string, TieringPartner[]>>({});
  const [currentTransform, setCurrentTransform] = useState<d3.ZoomTransform>(d3.zoomIdentity);
  const [showHint, setShowHint] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleClusterClick = async (category: string) => {
    if (expandedClusters.has(category)) {
      setExpandedClusters(prev => {
        const next = new Set(prev);
        next.delete(category);
        return next;
      });
      return;
    }
    try {
      const result = await TieringService.getPartnersByCategory(rootEntityId, category, 1, 50);
      setClusterPartners(prev => ({ ...prev, [category]: result.partners ?? [] }));
      setExpandedClusters(prev => { const next = new Set(prev); next.add(category); return next; });
    } catch {
      onClusterClick?.(category);
    }
  };

  useEffect(() => {
    if (!rootEntityId) return;
    setLoading(true);
    setError(null);
    TieringService.getTree(rootEntityId, 5)
      .then((data) => {
        setTreeData(data as unknown as TierNode);
        setLoading(false);
      })
      .catch(() => {
        TieringService.getHierarchy(rootEntityId)
          .then((data) => {
            setTreeData(data as unknown as TierNode);
            setLoading(false);
          })
          .catch((err) => {
            setError(String(err));
            setLoading(false);
          });
      });
  }, [rootEntityId]);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ w: Math.max(width, 400), h: Math.max(height, 500) });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Edge draw-in animation
  useEffect(() => {
    if (!svgRef.current || !treeData) return;
    const paths = svgRef.current.querySelectorAll('.tier-edge');
    paths.forEach((path, i) => {
      const el = path as SVGPathElement;
      const length = el.getTotalLength();
      el.setAttribute('stroke-dasharray', String(length));
      el.setAttribute('stroke-dashoffset', String(length));
      el.style.transition = `stroke-dashoffset ${500 + i * 80}ms ease-out`;
      setTimeout(() => el.setAttribute('stroke-dashoffset', '0'), 50);
    });
  }, [treeData]);

  // Zoom + pan
  useEffect(() => {
    if (!svgRef.current || !treeData) return;
    const svg = d3.select(svgRef.current);
    const g = svg.select<SVGGElement>('g.zoom-layer');
    const gNode = g.node();
    if (!gNode) return;

    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.15, 3.0])
      .on('zoom', (event) => {
        g.attr('transform', event.transform.toString());
        setCurrentTransform(event.transform);
      });

    zoomRef.current = zoom;
    svg.call(zoom);

    requestAnimationFrame(() => {
      try {
        const bounds = gNode.getBBox();
        if (bounds.width === 0 || bounds.height === 0) return;

        const svgEl = svgRef.current!;
        const w = svgEl.clientWidth || 800;
        const h = svgEl.clientHeight || 600;
        const padding = 60;
        const scale = Math.min(
          0.90,
          (w - padding * 2) / (bounds.width + padding),
          (h - padding * 2) / (bounds.height + padding),
        );
        const tx = w / 2 - scale * (bounds.x + bounds.width / 2);
        const ty = padding + scale * Math.abs(bounds.y);
        const t = d3.zoomIdentity.translate(tx, ty).scale(scale);

        svg.call(zoom.transform, t);
        setCurrentTransform(t);
      } catch (e) {
        console.warn('D3 zoom init failed:', e);
      }
    });

    return () => {
      svg.on('.zoom', null);
    };
  }, [treeData, dimensions]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!svgRef.current || !zoomRef.current) return;
      if (e.key === '+' || e.key === '=') {
        d3.select(svgRef.current)
          .transition().duration(200)
          .call(zoomRef.current.scaleBy, 1.3);
      }
      if (e.key === '-') {
        d3.select(svgRef.current)
          .transition().duration(200)
          .call(zoomRef.current.scaleBy, 0.77);
      }
      if (e.key === 'f' || e.key === 'F') {
        fitToScreen(svgRef, zoomRef);
      }
      if (e.key === 'Escape') {
        d3.select(svgRef.current)
          .transition().duration(400)
          .call(zoomRef.current.transform, d3.zoomIdentity);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  if (loading) {
    return (
      <div className={styles.loadingSkeleton}>
        <span className={styles.spinnerLg} />
        <div className={styles.loadingText}>Loading structure...</div>
      </div>
    );
  }

  if (error || !treeData) {
    return <Empty description="No tiering relationships found" style={{ padding: 64 }} />;
  }

  // Deep copy treeData and inject expanded partner nodes in place of cluster chips
  const expandedTreeData: TierNode = JSON.parse(JSON.stringify(treeData));
  if (expandedClusters.size > 0 && expandedTreeData.children?.length) {
    expandedTreeData.children = expandedTreeData.children.map(child => {
      if (child.isCluster && child.clusterCategory && expandedClusters.has(child.clusterCategory)) {
        const partners = clusterPartners[child.clusterCategory] ?? [];
        return {
          ...child,
          isCluster: false,
          children: partners.map(p => ({
            entityId: p.partnerId,
            entityName: p.partnerName,
            entityType: 'Partner',
            category: p.category,
            einSsn: p.einSsn,
            ownershipPct: Number(p.ownershipPct),
            tierLevel: 2,
            isCluster: false,
            clusterCount: 0,
            clusterCategory: null,
            children: [],
          })),
        };
      }
      return child;
    });
  }

  const root = d3.hierarchy(expandedTreeData);
  const treeLayout = d3.tree<TierNode>()
    .nodeSize([200, 180])
    .separation((a, b) => {
      if (a.depth !== b.depth) return 2.0;
      if (a.parent === b.parent) return 1.4;
      return 2.0;
    });
  treeLayout(root as d3.HierarchyNode<TierNode>);

  const nodes = (root as unknown as HierarchyNode).descendants();
  const links = (root as unknown as HierarchyNode).links() as HierarchyLink[];

  const linkPath = (source: HierarchyNode, target: HierarchyNode) => {
    const midY = (source.y + target.y) / 2;
    return `M ${source.x} ${source.y} C ${source.x} ${midY}, ${target.x} ${midY}, ${target.x} ${target.y}`;
  };

  // Minimap calculations
  const MINIMAP_W = 160;
  const MINIMAP_H = 100;
  const MINIMAP_PAD = 8;
  let minimapTransform = 'translate(0,0)';
  let mmScale = 1;
  let viewportX = 0, viewportY = 0, viewportW = MINIMAP_W, viewportH = MINIMAP_H;

  if (nodes.length > 0) {
    const allX = nodes.map(n => n.x);
    const allY = nodes.map(n => n.y);
    const minX = Math.min(...allX);
    const maxX = Math.max(...allX);
    const minY = Math.min(...allY);
    const maxY = Math.max(...allY);
    const treeW = (maxX - minX) || 1;
    const treeH = (maxY - minY) || 1;
    mmScale = Math.min(
      (MINIMAP_W - MINIMAP_PAD * 2) / treeW,
      (MINIMAP_H - MINIMAP_PAD * 2) / treeH
    );
    const ox = MINIMAP_PAD + (MINIMAP_W - MINIMAP_PAD * 2 - treeW * mmScale) / 2 - minX * mmScale;
    const oy = MINIMAP_PAD + (MINIMAP_H - MINIMAP_PAD * 2 - treeH * mmScale) / 2 - minY * mmScale;
    minimapTransform = `translate(${ox}, ${oy}) scale(${mmScale})`;

    const k = currentTransform.k || 1;
    viewportX = (-currentTransform.x / k) * mmScale + ox;
    viewportY = (-currentTransform.y / k) * mmScale + oy;
    viewportW = (dimensions.w / k) * mmScale;
    viewportH = (dimensions.h / k) * mmScale;
  }

  return (
    <div
      ref={containerRef}
      className={styles.flowWrapper}
      style={{ width: '100%', height: Math.max(dimensions.h, 500), minHeight: 500, position: 'relative' }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        style={{ background: 'transparent' }}
      >
        <defs>
          <linearGradient id="edgeGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F26522" stopOpacity={0.9} />
            <stop offset="100%" stopColor="#F26522" stopOpacity={0.3} />
          </linearGradient>
          <marker
            id="arrowhead"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#F26522" fillOpacity={0.8} />
          </marker>
          <filter id="rootGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <pattern id="dotGrid" width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="12" cy="12" r="0.8" fill="rgba(255,255,255,0.06)" />
          </pattern>
        </defs>

        {/* Dot grid background */}
        <rect width="100%" height="100%" fill="url(#dotGrid)" />

        <g className="zoom-layer">
          {/* Edges — behind nodes */}
          {links.map((link) => {
            const labelX = link.source.x * 0.35 + link.target.x * 0.65;
            const labelY = link.source.y * 0.35 + link.target.y * 0.65;
            const pct = link.target.data.ownershipPct;
            return (
              <g key={`link-${link.target.data.entityId}`}>
                <path
                  className="tier-edge"
                  d={linkPath(link.source, link.target)}
                  fill="none"
                  stroke="url(#edgeGradient)"
                  strokeWidth={2}
                  strokeOpacity={0.8}
                  markerEnd="url(#arrowhead)"
                />
                {pct > 0 && (
                  <g>
                    <rect
                      x={labelX - 22} y={labelY - 10}
                      width={44} height={20} rx={4}
                      fill="rgba(242,101,34,0.15)"
                      stroke="rgba(242,101,34,0.40)"
                      strokeWidth={0.5}
                    />
                    <text
                      x={labelX} y={labelY + 4}
                      textAnchor="middle" fontSize={11} fontWeight={600}
                      fill="#F26522" fontFamily="Inter, sans-serif"
                    >
                      {pct.toFixed(1)}%
                    </text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Nodes — on top of edges */}
          {nodes.map((node) => {
            const isHov = hoveredNode === node.data.entityId;
            const onEnter = () => setHoveredNode(node.data.entityId);
            const onLeave = () => setHoveredNode(null);

            if (node.depth === 0) {
              return <RootNode key={`node-${node.data.entityId}`} node={node} />;
            }
            if (node.data.isCluster || renderMode === 'clustered') {
              const category = node.data.clusterCategory ?? '';
              return (
                <ClusterNodeEl
                  key={`node-${node.data.entityId}`}
                  node={node}
                  onClusterClick={() => handleClusterClick(category)}
                  isHovered={isHov}
                  isExpanded={expandedClusters.has(category)}
                  onEnter={onEnter}
                  onLeave={onLeave}
                />
              );
            }
            return (
              <ChildNodeEl
                key={`node-${node.data.entityId}`}
                node={node}
                onClusterClick={onClusterClick}
                isHovered={isHov}
                onEnter={onEnter}
                onLeave={onLeave}
              />
            );
          })}
        </g>
      </svg>

      {/* Zoom controls — bottom-left */}
      <ZoomControls svgRef={svgRef} zoomRef={zoomRef} />

      {/* Minimap — bottom-right */}
      <div style={{
        position: 'absolute',
        bottom: 24,
        right: 24,
        width: MINIMAP_W,
        height: MINIMAP_H,
        background: 'rgba(10,10,20,0.85)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 8,
        overflow: 'hidden',
        backdropFilter: 'blur(8px)',
        zIndex: 10,
      }}>
        <svg width={MINIMAP_W} height={MINIMAP_H}>
          <g transform={minimapTransform}>
            {links.map((link, i) => (
              <line
                key={i}
                x1={link.source.x} y1={link.source.y}
                x2={link.target.x} y2={link.target.y}
                stroke="rgba(242,101,34,0.40)"
                strokeWidth={1 / mmScale}
              />
            ))}
            {nodes.map(node => (
              <circle
                key={node.data.entityId}
                cx={node.x} cy={node.y}
                r={(node.depth === 0 ? 4 : 2) / mmScale}
                fill={node.depth === 0 ? '#F26522' : 'rgba(255,255,255,0.50)'}
              />
            ))}
          </g>
          <rect
            x={viewportX} y={viewportY}
            width={Math.max(viewportW, 0)} height={Math.max(viewportH, 0)}
            fill="rgba(242,101,34,0.08)"
            stroke="#F26522"
            strokeWidth={1}
          />
        </svg>
        <div style={{
          position: 'absolute',
          top: 4,
          left: 6,
          fontSize: 9,
          color: 'rgba(255,255,255,0.35)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          pointerEvents: 'none',
        }}>
          Overview
        </div>
      </div>

      {/* Hint tooltip — fades after 3s */}
      {showHint && (
        <div style={{
          position: 'absolute',
          bottom: 70,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(10,10,20,0.85)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: 12,
          color: 'rgba(255,255,255,0.50)',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          transition: 'opacity 0.5s',
          zIndex: 10,
        }}>
          Scroll to zoom · Drag to pan · Press F to fit · Click clusters to expand
        </div>
      )}
    </div>
  );
};

export default D3TierDiagram;
